// Execution Analytics Dashboard (Phase 3)
// Performance, compliance, deviation, and risk analytics

import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Activity, BarChart3, Calendar, AlertCircle } from 'lucide-react';
import { useTasks } from '../../hooks/useTasks';
import { taskExecutionService } from '../../services/taskExecutionService';
import { complianceService } from '../../services/complianceService';
import { deviationService } from '../../services/deviationService';
import { riskCalculationService } from '../../services/riskCalculationService';
import './ExecutionAnalyticsDashboard.css';

const ExecutionAnalyticsDashboard = ({ userId, dateRange = { start: null, end: null } }) => {
  const [filters, setFilters] = useState({
    departmentId: '',
    protocolId: '',
    executorId: ''
  });
  
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('compliance'); // 'compliance', 'performance', 'deviations', 'risk'

  const { tasks, loading: tasksLoading, refresh } = useTasks({});

  useEffect(() => {
    loadAnalytics();
  }, [filters, dateRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      // Filter tasks by date range
      let filteredTasks = [...tasks];
      
      if (dateRange.start) {
        filteredTasks = filteredTasks.filter(task => {
          const taskDate = task.createdAt?.toDate ? task.createdAt.toDate() : new Date(task.createdAt);
          return taskDate >= dateRange.start;
        });
      }
      
      if (dateRange.end) {
        filteredTasks = filteredTasks.filter(task => {
          const taskDate = task.createdAt?.toDate ? task.createdAt.toDate() : new Date(task.createdAt);
          return taskDate <= dateRange.end;
        });
      }
      
      // Calculate analytics
      const analyticsData = calculateAnalytics(filteredTasks);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (tasks) => {
    const completedTasks = tasks.filter(t => t.status === 'completed');
    
    // Performance Metrics
    const avgCompletionTime = completedTasks.reduce((sum, task) => {
      if (task.actualEnd && task.actualStart) {
        const start = task.actualStart.toMillis ? task.actualStart.toMillis() : new Date(task.actualStart).getTime();
        const end = task.actualEnd.toMillis ? task.actualEnd.toMillis() : new Date(task.actualEnd).getTime();
        return sum + ((end - start) / 1000 / 60); // minutes
      }
      return sum;
    }, 0) / (completedTasks.length || 1);
    
    // Compliance Metrics
    const complianceScores = completedTasks
      .map(t => t.complianceScore)
      .filter(s => s !== null && s !== undefined);
    const avgCompliance = complianceScores.length > 0
      ? complianceScores.reduce((sum, score) => sum + score, 0) / complianceScores.length
      : 0;
    
    // Deviation Metrics
    const allDeviations = tasks.flatMap(t => t.deviations || []);
    const deviationByType = {};
    allDeviations.forEach(dev => {
      const type = dev.deviation_type || dev.type || 'unknown';
      deviationByType[type] = (deviationByType[type] || 0) + 1;
    });
    
    // Risk Metrics
    const highRiskTasks = tasks.filter(t => {
      const maxRPN = t.risk_data?.max_rpn || 0;
      return maxRPN >= 500;
    });
    
    // Compliance by Day of Week
    const complianceByDay = {};
    completedTasks.forEach(task => {
      if (task.complianceScore !== null && task.completedAt) {
        const date = task.completedAt.toDate ? task.completedAt.toDate() : new Date(task.completedAt);
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
        if (!complianceByDay[dayOfWeek]) {
          complianceByDay[dayOfWeek] = { sum: 0, count: 0 };
        }
        complianceByDay[dayOfWeek].sum += task.complianceScore;
        complianceByDay[dayOfWeek].count += 1;
      }
    });
    
    Object.keys(complianceByDay).forEach(day => {
      complianceByDay[day] = Math.round(complianceByDay[day].sum / complianceByDay[day].count);
    });
    
    return {
      performance: {
        totalTasks: tasks.length,
        completedTasks: completedTasks.length,
        completionRate: tasks.length > 0 ? (completedTasks.length / tasks.length * 100) : 0,
        avgCompletionTime: Math.round(avgCompletionTime),
        avgCompletionTimeHours: Math.round(avgCompletionTime / 60 * 10) / 10
      },
      compliance: {
        avgScore: Math.round(avgCompliance),
        scoresByDay: complianceByDay,
        scoreDistribution: {
          excellent: complianceScores.filter(s => s >= 90).length,
          good: complianceScores.filter(s => s >= 80 && s < 90).length,
          satisfactory: complianceScores.filter(s => s >= 70 && s < 80).length,
          needsImprovement: complianceScores.filter(s => s < 70).length
        }
      },
      deviations: {
        total: allDeviations.length,
        byType: deviationByType,
        critical: allDeviations.filter(d => d.severity === 'critical').length,
        major: allDeviations.filter(d => d.severity === 'major').length,
        minor: allDeviations.filter(d => d.severity === 'minor').length
      },
      risk: {
        totalTasks: tasks.length,
        highRiskTasks: highRiskTasks.length,
        highRiskPercentage: tasks.length > 0 ? (highRiskTasks.length / tasks.length * 100) : 0,
        avgMaxRPN: tasks.length > 0
          ? Math.round(tasks.reduce((sum, t) => sum + (t.risk_data?.max_rpn || 0), 0) / tasks.length)
          : 0
      }
    };
  };

  if (loading || tasksLoading) {
    return (
      <div className="analytics-loading">
        <div className="spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="analytics-empty">
        <p>No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="execution-analytics-dashboard">
      <div className="dashboard-header">
        <div>
          <h2>Execution Analytics</h2>
          <p className="subtitle">Performance, compliance, and risk analytics</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={loadAnalytics}>
            Refresh
          </button>
        </div>
      </div>

      {/* Metric Tabs */}
      <div className="metric-tabs">
        <button
          className={`metric-tab ${selectedMetric === 'performance' ? 'active' : ''}`}
          onClick={() => setSelectedMetric('performance')}
        >
          <Activity size={16} /> Performance
        </button>
        <button
          className={`metric-tab ${selectedMetric === 'compliance' ? 'active' : ''}`}
          onClick={() => setSelectedMetric('compliance')}
        >
          <TrendingUp size={16} /> Compliance
        </button>
        <button
          className={`metric-tab ${selectedMetric === 'deviations' ? 'active' : ''}`}
          onClick={() => setSelectedMetric('deviations')}
        >
          <AlertCircle size={16} /> Deviations
        </button>
        <button
          className={`metric-tab ${selectedMetric === 'risk' ? 'active' : ''}`}
          onClick={() => setSelectedMetric('risk')}
        >
          <BarChart3 size={16} /> Risk
        </button>
      </div>

      {/* Performance Metrics */}
      {selectedMetric === 'performance' && (
        <div className="analytics-section">
          <h3>Performance Metrics</h3>
          
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-label">Total Tasks</div>
              <div className="metric-value">{analytics.performance.totalTasks}</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Completed Tasks</div>
              <div className="metric-value">{analytics.performance.completedTasks}</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Completion Rate</div>
              <div className="metric-value">{Math.round(analytics.performance.completionRate)}%</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Avg Completion Time</div>
              <div className="metric-value">
                {analytics.performance.avgCompletionTimeHours > 0 
                  ? `${analytics.performance.avgCompletionTimeHours}h`
                  : `${analytics.performance.avgCompletionTime} min`
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Metrics */}
      {selectedMetric === 'compliance' && (
        <div className="analytics-section">
          <h3>Compliance Analytics</h3>
          
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-label">Average Compliance Score</div>
              <div className="metric-value">{analytics.compliance.avgScore}%</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Excellent (≥90%)</div>
              <div className="metric-value" style={{ color: '#28a745' }}>
                {analytics.compliance.scoreDistribution.excellent}
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Good (80-89%)</div>
              <div className="metric-value" style={{ color: '#5cb85c' }}>
                {analytics.compliance.scoreDistribution.good}
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Satisfactory (70-79%)</div>
              <div className="metric-value" style={{ color: '#ffc107' }}>
                {analytics.compliance.scoreDistribution.satisfactory}
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Needs Improvement (&lt;70%)</div>
              <div className="metric-value" style={{ color: '#dc3545' }}>
                {analytics.compliance.scoreDistribution.needsImprovement}
              </div>
            </div>
          </div>
          
          {/* Compliance by Day */}
          {Object.keys(analytics.compliance.scoresByDay).length > 0 && (
            <div className="compliance-chart">
              <h4>Compliance by Day of Week</h4>
              <div className="bar-chart">
                {Object.entries(analytics.compliance.scoresByDay).map(([day, score]) => (
                  <div key={day} className="bar-item">
                    <div className="bar-label">{day}</div>
                    <div className="bar-container">
                      <div 
                        className="bar-fill"
                        style={{ 
                          width: `${score}%`,
                          backgroundColor: score >= 90 ? '#28a745' : score >= 80 ? '#5cb85c' : score >= 70 ? '#ffc107' : '#dc3545'
                        }}
                      />
                      <span className="bar-value">{score}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Deviation Metrics */}
      {selectedMetric === 'deviations' && (
        <div className="analytics-section">
          <h3>Deviation Analytics</h3>
          
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-label">Total Deviations</div>
              <div className="metric-value">{analytics.deviations.total}</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Critical</div>
              <div className="metric-value" style={{ color: '#dc3545' }}>
                {analytics.deviations.critical}
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Major</div>
              <div className="metric-value" style={{ color: '#fd7e14' }}>
                {analytics.deviations.major}
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Minor</div>
              <div className="metric-value" style={{ color: '#ffc107' }}>
                {analytics.deviations.minor}
              </div>
            </div>
          </div>
          
          {/* Deviation by Type */}
          {Object.keys(analytics.deviations.byType).length > 0 && (
            <div className="deviation-breakdown">
              <h4>Deviations by Type</h4>
              <div className="deviation-list">
                {Object.entries(analytics.deviations.byType).map(([type, count]) => (
                  <div key={type} className="deviation-type-item">
                    <span className="deviation-type-name">{type}</span>
                    <span className="deviation-type-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Risk Metrics */}
      {selectedMetric === 'risk' && (
        <div className="analytics-section">
          <h3>Risk Analytics</h3>
          
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-label">Total Tasks</div>
              <div className="metric-value">{analytics.risk.totalTasks}</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">High-Risk Tasks</div>
              <div className="metric-value" style={{ color: '#dc3545' }}>
                {analytics.risk.highRiskTasks}
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">High-Risk Percentage</div>
              <div className="metric-value">{Math.round(analytics.risk.highRiskPercentage)}%</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-label">Average Max RPN</div>
              <div className="metric-value">{analytics.risk.avgMaxRPN}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutionAnalyticsDashboard;

